import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createContext, ReactNode } from 'react';
import { useGetAdjacentStores, useGetStore } from '../../api/store/query';
import UserStorePanel from '../../components-legacy/UserStorePanel';
import PanelStoreInfo from '../../components-legacy/UserStorePanel/PanelStoreInfo';
import useContext from '../../hooks/useContext';
import useToggle from '../../hooks/useToggle';
import { toUiStore } from '../../utils/store';
import { PostalContext } from '../postal';

interface IStoreContext {
  loading: boolean;
  error: boolean;
  userStore: IUiStore | undefined;
  updateUserStore: (storeId: string) => void;
  adjacentUserStores: IUiStore[] | undefined;

  showUserStorePanel: boolean;
  toggleShowUserStorePanel: () => void;
}

export const StoreContext = createContext<IStoreContext | undefined>(undefined);

interface IStoreProvider {
  children: ReactNode;
}

const LOCAL_STORAGE_STORE_KEY = 'USER_STORE';

const initialUserStore = localStorage.getItem(LOCAL_STORAGE_STORE_KEY);

const StoreProvider = ({ children }: IStoreProvider) => {
  const [internalUserStore, setInternalUserStore] = useState(initialUserStore);

  const {
    data: store,
    isLoading: loadingStore,
    isError: storeError,
  } = useGetStore<IStore>(internalUserStore);

  const {
    data: adjacentStores,
    isLoading: loadingAdjacentStores,
    isError: adjacentStoreError,
  } = useGetAdjacentStores(internalUserStore);

  const [userStore, setUserStore] = useState(store && toUiStore(store));
  const [adjacentUserStores, setAdjacentUserStores] = useState(adjacentStores?.map(toUiStore));

  const [showUserStorePanel, toggleShowUserStorePanel] = useToggle(false);
  const { postal, postalInformation } = useContext(PostalContext);

  useEffect(() => {
    // Om postnummer anges och ingen butik är vald så sätter vi butiken kopplat till postnummret, annars gör vi inget.
    if (!!postalInformation && !internalUserStore) {
      updateUserStore(postalInformation?.storeId);
    }
  }, [postal, postalInformation]);

  useEffect(() => {
    if (adjacentStores && store) {
      setAdjacentUserStores(adjacentStores?.map(toUiStore));
      setUserStore(toUiStore(store));
    }
  }, [adjacentStores, store]);

  const updateUserStore = useCallback((storeId: string) => {
    localStorage.setItem(LOCAL_STORAGE_STORE_KEY, storeId);
    setInternalUserStore(storeId);
  }, []);

  const value = useMemo(
    () => ({
      loading: loadingAdjacentStores || loadingStore || (!!internalUserStore && !userStore),
      error: storeError || adjacentStoreError,
      userStore,
      updateUserStore,
      adjacentUserStores,

      showUserStorePanel,
      toggleShowUserStorePanel,
    }),
    [
      showUserStorePanel,
      toggleShowUserStorePanel,
      userStore,
      updateUserStore,
      adjacentUserStores,
      loadingAdjacentStores,
      loadingStore,
      storeError,
      adjacentStoreError,
    ],
  );

  return (
    <StoreContext.Provider value={value}>
      {children}
      {showUserStorePanel && (
        <UserStorePanel
          onClose={toggleShowUserStorePanel}
          onSelectStore={updateUserStore}
          selectedStoreId={userStore?.storeId}
          panelTitle="Välj butik"
          renderStatus={(store) => <PanelStoreInfo store={store} />}
        />
      )}
    </StoreContext.Provider>
  );
};

export default StoreProvider;
